// @ts-check
// Regras puras do painel de suporte (contrato 08 de cobrança e operação, seção 7.4): seções e itens do menu lateral,
// caminho de cada tela e cores das situações do Asaas. Sem DOM, para os testes de cliente.

/**
 * @typedef {'overview' | 'institutions' | 'payments' | 'invoices' | 'site-orders' | 'coupons' | 'access-codes' | 'email-sending' | 'backups' | 'audit'} AdminSectionKey
 * @typedef {{ key: AdminSectionKey, icon: string }} AdminNavigationItem
 * @typedef {{ key: 'customers' | 'finance' | 'platform', items: ReadonlyArray<AdminNavigationItem> }} AdminNavigationGroup
 */

export const ADMIN_BASE_PATH = '/admin';

/** @param {AdminSectionKey} key @param {string} icon @returns {AdminNavigationItem} */
const item = (key, icon) => Object.freeze({ key, icon });

/** @type {ReadonlyArray<AdminNavigationGroup>} */
export const ADMIN_NAVIGATION = Object.freeze([
  { key: 'customers', items: Object.freeze([item('overview', 'IconLayoutDashboard'), item('institutions', 'IconBuilding')]) },
  { key: 'finance', items: Object.freeze([item('payments', 'IconReceipt'), item('invoices', 'IconFileText'), item('site-orders', 'IconCash'), item('coupons', 'IconTicket')]) },
  { key: 'platform', items: Object.freeze([item('access-codes', 'IconKey'), item('email-sending', 'IconMail'), item('backups', 'IconDatabase'), item('audit', 'IconHistory')]) },
]);

const SECTION_KEYS = new Set(ADMIN_NAVIGATION.flatMap((group) => group.items.map((item) => item.key)));

/** Abas da página da instituição, na ordem da tela. */
export const ADMIN_INSTITUTION_TABS = Object.freeze(['summary', 'subscription', 'payments', 'invoices', 'team', 'operations', 'support']);

/**
 * Seção e id do caminho do painel: `/admin` é a visão geral, `/admin/institutions/<id>` a instituição, e qualquer
 * seção desconhecida volta para a visão geral (o link antigo /admin continua valendo).
 * @param {{ section?: string, id?: string }} params
 * @returns {{ section: AdminSectionKey, id: string | null }}
 */
export function resolveAdminRoute(params) {
  const section = params.section && SECTION_KEYS.has(/** @type {AdminSectionKey} */ (params.section)) ? /** @type {AdminSectionKey} */ (params.section) : 'overview';
  return { section, id: section === 'overview' ? null : params.id ?? null };
}

/** @param {AdminSectionKey} section @param {string | null} [id] */
export function adminPath(section, id = null) {
  if (section === 'overview') return ADMIN_BASE_PATH;
  return id ? `${ADMIN_BASE_PATH}/${section}/${encodeURIComponent(id)}` : `${ADMIN_BASE_PATH}/${section}`;
}

/** @param {string} tenantId @param {string} [tab] */
export function adminInstitutionPath(tenantId, tab) {
  const base = adminPath('institutions', tenantId);
  return tab && tab !== ADMIN_INSTITUTION_TABS[0] ? `${base}?tab=${tab}` : base;
}

/** Aba da instituição lida da URL; desconhecida volta ao resumo. @param {string | null | undefined} value */
export function resolveInstitutionTab(value) {
  return value && ADMIN_INSTITUTION_TABS.includes(value) ? value : ADMIN_INSTITUTION_TABS[0];
}

/** Cor da etiqueta de situação de uma cobrança do Asaas. @param {string | null | undefined} status */
export function paymentStatusColor(status) {
  switch (status) {
    case 'RECEIVED':
    case 'CONFIRMED':
    case 'RECEIVED_IN_CASH':
    case 'DUNNING_RECEIVED':
      return 'green';
    case 'PENDING':
    case 'AWAITING_RISK_ANALYSIS':
      return 'blue';
    case 'OVERDUE':
    case 'DUNNING_REQUESTED':
      return 'orange';
    case 'REFUNDED':
    case 'PARTIALLY_REFUNDED':
    case 'REFUND_REQUESTED':
    case 'REFUND_IN_PROGRESS':
      return 'purple';
    case 'CHARGEBACK_REQUESTED':
    case 'CHARGEBACK_DISPUTE':
    case 'AWAITING_CHARGEBACK_REVERSAL':
      return 'red';
    default:
      return 'gray';
  }
}

/** Cor da etiqueta de situação de uma nota fiscal. @param {string | null | undefined} status */
export function invoiceStatusColor(status) {
  switch (status) {
    case 'AUTHORIZED':
      return 'green';
    case 'SCHEDULED':
      return 'blue';
    case 'PROCESSING_CANCELLATION':
    case 'CANCELLATION_DENIED':
      return 'orange';
    case 'ERROR':
      return 'red';
    case 'CANCELED':
    case 'CANCELLED':
      return 'gray';
    default:
      return 'gray';
  }
}

/** Cor da etiqueta de situação de um pedido do site. @param {string | null | undefined} status */
export function siteOrderStatusColor(status) {
  return { PENDING: 'blue', CONFIRMED: 'green', OVERDUE: 'orange', CANCELED: 'gray' }[String(status)] ?? 'gray';
}

/** A cobrança pode ser estornada (paga no cartão ou no Pix e ainda não estornada por inteiro). @param {{ status?: string, billingType?: string }} payment */
export function canRefundPayment(payment) {
  return ['RECEIVED', 'CONFIRMED', 'PARTIALLY_REFUNDED'].includes(String(payment.status)) && ['CREDIT_CARD', 'PIX'].includes(String(payment.billingType));
}

/** A cobrança em aberto pode ser removida sem estorno. @param {{ status?: string }} payment */
export function canDeletePayment(payment) {
  return ['PENDING', 'OVERDUE'].includes(String(payment.status));
}

/** A cobrança já tem dinheiro recebido e pode ter nota fiscal. @param {{ status?: string }} payment */
export function canInvoicePayment(payment) {
  return ['RECEIVED', 'CONFIRMED', 'RECEIVED_IN_CASH', 'PENDING', 'OVERDUE'].includes(String(payment.status));
}

/**
 * Impostos da nota como a API pede: todos presentes, percentuais numéricos, 0 quando o campo está vazio.
 * @param {Record<string, string | number | boolean | null | undefined>} draft
 */
export function normalizeInvoiceTaxes(draft) {
  const number = (/** @type {unknown} */ value) => {
    const parsed = Number(String(value ?? '').replace(',', '.'));
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  };
  return {
    retainIss: draft.retainIss === true, iss: number(draft.iss), pis: number(draft.pis), cofins: number(draft.cofins), csll: number(draft.csll), inss: number(draft.inss), ir: number(draft.ir),
  };
}

/**
 * Filtro de texto da lista de instituições: nome, endereço (slug) e e-mail de quem a criou, sem acento.
 * @param {{ displayName: string, slug: string, ownerEmail?: string | null }} tenant @param {string} query
 */
export function matchesInstitutionQuery(tenant, query) {
  const normalized = normalizeText(query);
  if (!normalized) return true;
  return [tenant.displayName, tenant.slug, tenant.ownerEmail ?? ''].some((value) => normalizeText(value).includes(normalized));
}

/** @param {string} value */
function normalizeText(value) {
  return String(value ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();
}
